import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Bell, BellOff, Download, X } from 'lucide-react';
import usePushNotifications from '../hooks/usePushNotifications';
import { toast } from 'sonner';

const PWAInstallBanner = ({ language = 'it' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe
  } = usePushNotifications();

  // Check if app is installed
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;
    setIsInstalled(isStandalone);
  }, []);

  // Listen for install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install banner if not dismissed before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Show notification banner after install or on first visit
  useEffect(() => {
    if (isSupported && !isSubscribed && permission !== 'denied') {
      const dismissed = localStorage.getItem('push-banner-dismissed');
      const timer = setTimeout(() => {
        if (!dismissed && !showInstallBanner) {
          setShowNotificationBanner(true);
        }
      }, 3000); // Show after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isSupported, isSubscribed, permission, showInstallBanner]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success(language === 'it' ? 'App installata!' : 'App installed!');
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleEnableNotifications = async () => {
    const success = await subscribe();
    if (success) {
      toast.success(
        language === 'it' 
          ? 'Notifiche attivate! Riceverai promemoria per i tuoi appuntamenti.' 
          : 'Notifications enabled! You will receive reminders for your appointments.'
      );
      setShowNotificationBanner(false);
    } else {
      toast.error(
        language === 'it' 
          ? 'Impossibile attivare le notifiche' 
          : 'Could not enable notifications'
      );
    }
  };

  const handleDisableNotifications = async () => {
    const success = await unsubscribe();
    if (success) {
      toast.success(
        language === 'it' 
          ? 'Notifiche disattivate' 
          : 'Notifications disabled'
      );
    }
  };

  const handleDismissNotification = () => {
    setShowNotificationBanner(false);
    localStorage.setItem('push-banner-dismissed', 'true');
  };

  // Render install banner
  if (showInstallBanner && deferredPrompt && !isInstalled) {
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-card border rounded-xl shadow-lg p-4 z-50 animate-slide-up">
        <button 
          onClick={handleDismissInstall}
          className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6 text-accent" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium">
              {language === 'it' ? 'Installa SmartBooker' : 'Install SmartBooker'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'it' 
                ? 'Aggiungi l\'app alla home per un accesso rapido' 
                : 'Add to home screen for quick access'}
            </p>
            <Button 
              onClick={handleInstall}
              size="sm"
              className="mt-3"
              data-testid="install-pwa-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {language === 'it' ? 'Installa' : 'Install'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render notification banner
  if (showNotificationBanner && isSupported && !isSubscribed && permission !== 'denied') {
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-card border rounded-xl shadow-lg p-4 z-50 animate-slide-up">
        <button 
          onClick={handleDismissNotification}
          className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <Bell className="w-6 h-6 text-amber-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium">
              {language === 'it' ? 'Attiva le notifiche' : 'Enable notifications'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'it' 
                ? 'Ricevi promemoria per i tuoi appuntamenti' 
                : 'Get reminders for your appointments'}
            </p>
            <Button 
              onClick={handleEnableNotifications}
              size="sm"
              className="mt-3"
              disabled={loading}
              data-testid="enable-notifications-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              {loading 
                ? (language === 'it' ? 'Attivazione...' : 'Enabling...') 
                : (language === 'it' ? 'Attiva' : 'Enable')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Notification toggle for settings
export const NotificationToggle = ({ language = 'it', compact = false }) => {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe
  } = usePushNotifications();

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success(
          language === 'it' 
            ? 'Notifiche disattivate' 
            : 'Notifications disabled'
        );
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success(
          language === 'it' 
            ? 'Notifiche attivate!' 
            : 'Notifications enabled!'
        );
      }
    }
  };

  if (!isSupported) {
    if (compact) return null;
    return (
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
        <div>
          <p className="font-medium">{language === 'it' ? 'Notifiche Push' : 'Push Notifications'}</p>
          <p className="text-sm text-muted-foreground">
            {language === 'it' 
              ? 'Non supportate dal tuo browser' 
              : 'Not supported by your browser'}
          </p>
        </div>
        <BellOff className="w-5 h-5 text-muted-foreground" />
      </div>
    );
  }

  if (permission === 'denied') {
    if (compact) {
      return (
        <button
          disabled
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-muted-foreground bg-muted/50"
          title={language === 'it' ? 'Notifiche bloccate dal browser' : 'Notifications blocked by browser'}
        >
          <BellOff className="w-4 h-4" />
          <span className="text-sm">{language === 'it' ? 'Bloccate' : 'Blocked'}</span>
        </button>
      );
    }
    return (
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
        <div>
          <p className="font-medium">{language === 'it' ? 'Notifiche Push' : 'Push Notifications'}</p>
          <p className="text-sm text-muted-foreground">
            {language === 'it' 
              ? 'Bloccate nelle impostazioni del browser' 
              : 'Blocked in browser settings'}
          </p>
        </div>
        <BellOff className="w-5 h-5 text-destructive" />
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
          isSubscribed 
            ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30' 
            : 'hover:bg-muted'
        }`}
        data-testid="notification-toggle-compact"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isSubscribed ? (
          <Bell className="w-4 h-4" />
        ) : (
          <BellOff className="w-4 h-4" />
        )}
        <span className="text-sm">
          {isSubscribed 
            ? (language === 'it' ? 'Notifiche ON' : 'Notifications ON')
            : (language === 'it' ? 'Notifiche OFF' : 'Notifications OFF')
          }
        </span>
      </button>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
      <div>
        <p className="font-medium">{language === 'it' ? 'Notifiche Push' : 'Push Notifications'}</p>
        <p className="text-sm text-muted-foreground">
          {isSubscribed 
            ? (language === 'it' ? 'Ricevi promemoria appuntamenti' : 'Receive appointment reminders')
            : (language === 'it' ? 'Attiva per ricevere promemoria' : 'Enable to receive reminders')}
        </p>
      </div>
      <Button
        variant={isSubscribed ? "outline" : "default"}
        size="sm"
        onClick={handleToggle}
        disabled={loading}
        data-testid="notification-toggle-btn"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isSubscribed ? (
          <>
            <BellOff className="w-4 h-4 mr-2" />
            {language === 'it' ? 'Disattiva' : 'Disable'}
          </>
        ) : (
          <>
            <Bell className="w-4 h-4 mr-2" />
            {language === 'it' ? 'Attiva' : 'Enable'}
          </>
        )}
      </Button>
    </div>
  );
};

export default PWAInstallBanner;
